import {
    BaseQueryApi,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
    fetchBaseQuery,
} from '@reduxjs/toolkit/query';
import { API_BASE_URL } from '@shared/config/api/api-base';
import { API_AUTH_PREFIX } from '@shared/config/api/api-prefixes';
import { getCredentials, removeCredentials, setCredentials } from '@shared/lib/utils/credentials/credentials';
import { RefreshTokenDataResponse } from './types';
import { API_PATHS_WITHOUT_CREDENTIALS } from '@shared/config/api/api-paths';
import { ROUTE_BLOCKED, ROUTE_NOT_VERIFIED } from '@shared/config/routes/routes';
import { USER_BLOCKED, USER_NOT_VERIFIED } from './constants';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import { setNotVerifiedLoginValueToStorage } from '../lib/login-not-verified-value-storage';

interface RequestQueueItem {
    resolve: (value: unknown) => void;
    reject: (value: unknown) => void;
    args: string | FetchArgs;
    api: BaseQueryApi;
    extraOptions: object;
}

const baseQueryWithAuth = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers) => {
        const { accessToken } = getCredentials();
        const contentType = headers.get('Content-Type');
        const isFormData = contentType?.startsWith('multipart/form-data');

        if (accessToken) {
            headers.append('Authorization', `Bearer ${accessToken}`);
        }
        if (!headers.has('Content-Type')) {
            headers.append('Content-Type', 'application/json');
        }
        if (isFormData) {
            headers.delete('Content-Type');
        }
        return headers;
    },
});

let isTokensRefreshing = false;
const requestQueue: RequestQueueItem[] = [];

export const baseQueryWithReauth = async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: object) => {
    let result = await baseQueryWithAuth(args, api, extraOptions);
    const url = typeof args === 'string' ? '' : args.url;
    const isNonCredentialUrl = Boolean(API_PATHS_WITHOUT_CREDENTIALS.find((path) => url.includes(path)));

    if (result.meta?.response?.headers.has('X-Error-Type')) {
        const header = result?.meta?.response?.headers.get('X-Error-Type');

        if (header === `${USER_BLOCKED}`) {
            removeCredentials();
            window.location.replace(ROUTE_BLOCKED);
        }

        if (header === `${USER_NOT_VERIFIED}` && typeof args !== 'string') {
            setNotVerifiedLoginValueToStorage(args?.body?.email);
            removeCredentials();
            window.location.replace(ROUTE_NOT_VERIFIED);
        }
    }

    if (result.error && result.error.status === 401 && !isNonCredentialUrl) {
        const { accessToken, refreshToken } = getCredentials();

        if (isTokensRefreshing && !isNonCredentialUrl) {
            const result = await new Promise<QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>>(
                (resolve, reject) => {
                    requestQueue.push({ resolve, reject, args, api, extraOptions });
                }
            );
            return result;
        }
        // Оказывается бэку для рефреша нужны оба токена
        if (accessToken && refreshToken) {
            isTokensRefreshing = true;
            const refreshResult = await baseQueryWithAuth(
                {
                    url: `${API_AUTH_PREFIX}/refresh-token`,
                    method: 'POST',
                    body: { refreshToken },
                    headers: { 'Content-Type': 'application/json' },
                },
                api,
                extraOptions
            );
            const data = refreshResult.data as RefreshTokenDataResponse;
            if (!data) {
                removeCredentials();
                location.reload();
            }
            setCredentials({ accessToken: data.accessToken, refreshToken: data.refreshToken });
            isTokensRefreshing = false;
            Promise.all(
                requestQueue.map(async (request) => {
                    try {
                        const result = await baseQueryWithAuth(request.args, request.api, request.extraOptions);
                        request.resolve(result);
                    } catch (error) {
                        request.reject(error);
                    }
                })
            );
            result = await baseQueryWithAuth(args, api, extraOptions);
        } else {
            removeCredentials();
            location.reload();
        }
    }
    return result;
};
